import "./Breadcrumbs.scss";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import { FileEntity, Space, Track } from "@highnote/server/src/core/entities";
import { Button, BUTTON_THEME } from "App/core/Button";
import { useAuth } from "App/components/Auth";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { WatcherComponentId } from "App/store/watchers/useWatcherV2";
import { useViewport } from "./useViewport";

type Props = {
  activeSpace?: Space;
  activeTrack?: Track;
  activeTrackVersion?: FileEntity;
  onEntityClick?: (space?: Space) => void;
  hideLibrary?: boolean;
  componentId: WatcherComponentId;
};

export const Breadcrumbs = ({
  activeSpace,
  activeTrack,
  activeTrackVersion,
  onEntityClick,
  hideLibrary,
  componentId,
}: Props) => {
  const history = useHistory();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const { isMobile, isMaxViewportML } = useViewport();
  const {
    manageGlobalSingleSpaceWatcher,
    globalSpacesWatchers,
    globalSpaces,
    getGlobalSpace,
  } = useGlobalSpaces();
  const [fullPathIds, setFullPathIds] = useState<string[]>([]);

  useEffect(() => {
    let isMounted = true;
    const watchSpaces: string[] = []; // This will store spaceIds for which watchers are attached

    // This solution recursively fetches the parent spaces of the activeSpace
    // to be able to display the full path in the breadcrumbs as well as
    // stay synced to any realtime updates to the spaces.
    // TODO(Optimization): Look into an alternative solution that maybe renders a component recursively instead?
    // https://www.notion.so/highnotefm/Breadcrumbs-Optimization-Exploration-Recursive-rendering-of-parents-7d4b81a20a634635b6a23ea044ee91d3?pvs=4
    const getFullPath = async (_activeSpace?: Space) => {
      const findPath = async (
        currentSpace?: Space,
        path: Space[] = [],
      ): Promise<Space[]> => {
        if (!currentSpace?.spaceId) {
          return path;
        }

        let parentSpace = getGlobalSpace(currentSpace.spaceId);

        if (!parentSpace && !globalSpacesWatchers.has(currentSpace.spaceId)) {
          try {
            // Await for the space fetch and ensure the component is still mounted before proceeding
            await new Promise<void>((resolve) => {
              manageGlobalSingleSpaceWatcher({
                action: "attach",
                componentId,
                spaceId: currentSpace.spaceId,
                onSpaceAdd: (space) => {
                  if (isMounted) {
                    parentSpace = space;
                    resolve();
                  }
                },
              });
              watchSpaces.push(currentSpace.spaceId); // Store spaceId for later cleanup
            });
          } catch (error) {
            console.error("Error fetching space:", error);
            return path; // Return current path and stop further recursion
          }
        }

        if (parentSpace) {
          return findPath(parentSpace, [parentSpace, ...path]);
        }

        return path;
      };

      return findPath(_activeSpace);
    };

    getFullPath(activeSpace)
      .then((path) => {
        if (isMounted) {
          const pathIds = path.map((space) => space.id);
          setFullPathIds(pathIds);
        }
      })
      .catch((e) => {
        console.error("Error getting breadcrumb path:", e);
        if (isMounted) {
          setFullPathIds([]); // Reset the path to empty array in case of error
        }
      });

    return () => {
      isMounted = false; // Set the flag to false when the component unmounts
      // Detach all watchers on component unmount
      watchSpaces.forEach((spaceId) => {
        manageGlobalSingleSpaceWatcher({
          action: "detach",
          spaceId,
          componentId,
        });
      });
    };
  }, [activeSpace, hideLibrary, globalSpaces.spaces]);

  const isSpacePage = activeSpace && !(activeTrack || activeTrackVersion);
  const isActiveLibrary = !activeSpace && !activeTrack && !activeTrackVersion;

  // Don't show breadcrumbs on space page if not logged in
  if (!isLoggedIn && isSpacePage) {
    return null;
  }

  if (isActiveLibrary) {
    return null;
  }

  if (isMobile || isMaxViewportML) {
    // Don't show breadcrumbs on mobile if not logged in
    if (!isLoggedIn && isMobile) {
      return null;
    }

    // Show breadcrumbs on mobile and tablet if logged in
    // Only show the immediate parent space on mobile and tablet
    if (fullPathIds.length > 0 || activeTrack || activeTrackVersion) {
      const parentSpaceEntity = getGlobalSpace(activeSpace?.spaceId);

      if (parentSpaceEntity?.id) {
        const defaultClickPath = `/space/${parentSpaceEntity.id}`;

        if (onEntityClick) {
          return (
            <Button
              theme={BUTTON_THEME.TEXT}
              onClick={() => {
                onEntityClick(parentSpaceEntity);
              }}
            >
              {parentSpaceEntity.name}
            </Button>
          );
        }

        return (
          <div className="highnote-breadcrumbs highnote-breadcrumbs-mobile">
            <Link
              className="highnote-breadcrumbs-link highnote-breadcrumbs-link-mobile"
              to={defaultClickPath}
              title={parentSpaceEntity.name}
            >
              {parentSpaceEntity.name}
            </Link>
          </div>
        );
      }
    } else {
      if (hideLibrary) {
        return null;
      }

      if (onEntityClick) {
        return (
          <Button
            theme={BUTTON_THEME.TEXT}
            onClick={() => onEntityClick(undefined)}
          >
            Library
          </Button>
        );
      }
      // If there is no immediate parent space, show the library link for logged in users
      return (
        <div className="highnote-breadcrumbs highnote-breadcrumbs-mobile">
          <Link
            className="highnote-breadcrumbs-link highnote-breadcrumbs-link-mobile"
            to="/library"
          >
            Library
          </Link>
        </div>
      );
    }
  }

  return (
    <div
      className="highnote-breadcrumbs"
      data-cypress-id="highnote-breadcrumbs"
    >
      <MuiBreadcrumbs
        separator={<div className="divider">/</div>}
        aria-label="breadcrumb"
        // maxItems, itemsBeforeCollapse, itemsAfterCollapse props are needed to collapse the breadcrumbs.
        // We use them for spacing purposes. Adjust as needed.
        maxItems={2}
        itemsBeforeCollapse={1}
        itemsAfterCollapse={1}
        className="highnote-breadcrumbs-mui"
      >
        {!hideLibrary && !onEntityClick && (
          <Link className="highnote-breadcrumbs-link" to="/library">
            Library
          </Link>
        )}

        {!hideLibrary && onEntityClick && (
          <Button
            theme={BUTTON_THEME.PLAIN}
            onClick={() => onEntityClick(undefined)}
          >
            Library
          </Button>
        )}

        {fullPathIds.map((entityId) => {
          const entity = getGlobalSpace(entityId);
          const isClickable = activeSpace && entity.id !== activeSpace.id;
          const defaultClickPath = `/space/${entity.id}`;

          return (
            <Button
              key={entity.id}
              theme={BUTTON_THEME.PLAIN}
              disabled={!isClickable}
              onClick={() => {
                if (!isClickable) return;
                onEntityClick
                  ? onEntityClick(entity)
                  : history.push(defaultClickPath);
              }}
            >
              {entity.name}
            </Button>
          );
        })}

        {activeSpace && !activeTrack ? (
          <span
            className="highnote-breadcrumbs-active-link"
            title={activeSpace.name}
          >
            {activeSpace.name}
          </span>
        ) : onEntityClick ? (
          <Button
            theme={BUTTON_THEME.PLAIN}
            onClick={() => onEntityClick(activeSpace)}
          >
            {activeSpace?.name}
          </Button>
        ) : (
          <Link
            className="highnote-breadcrumbs-link"
            to={`/space/${activeSpace?.id}`}
            title={activeSpace?.name}
          >
            {activeSpace?.name}
          </Link>
        )}

        {/* Don't show active track on tablet screen size */}
        {activeTrack && !isMaxViewportML && (
          <span
            className="highnote-breadcrumbs-active-link"
            title={activeTrack.title}
          >
            {activeTrack.title}
          </span>
        )}

        {/* Don't show active track version on tablet screen size */}
        {activeTrackVersion && !isMaxViewportML && (
          <span
            className="highnote-breadcrumbs-active-link"
            title={activeTrackVersion.name}
          >
            {activeTrackVersion.name}
          </span>
        )}
      </MuiBreadcrumbs>
    </div>
  );
};
